const objective = (subtopicId, sourceSubtopicId, name, tier = "core") => ({
  subtopicId,
  sourceSubtopicId,
  name,
  tier,
});

export default {
  schemaVersion: 1,
  id: "cambridge-igcse-physics-waves-v1",
  subject: "Physics",
  chapter: "Waves",
  title: "Waves",
  subtitle: "Build the ideas first, then use the mapped Saras resources to explore and practise them.",
  topics: [
    {
      key: "general-properties",
      title: "General properties of waves",
      topicRef: { topicId: 165, sourceTopicId: 216, name: "General properties of waves" },
      sections: [
        {
          key: "describing-waves",
          title: "Describing Waves",
          intro: "Start with the central idea: a wave transfers energy while the material itself does not travel with the wave. Then connect that idea to vibration, wave shape, and the difference between transverse and longitudinal motion.",
          keyIdeas: [
            "Waves transfer energy without transporting matter from source to destination.",
            "Amplitude, wavelength, frequency, crest, trough and wavefront describe a wave.",
            "Transverse and longitudinal waves differ by the direction of vibration.",
          ],
          objectives: [
            objective(14, 10, "Core - Wave Energy Transfer"),
            objective(15, 11, "Core - Wave Motion and Vibrations"),
            objective(16, 12, "Core - Wave Features and Terminology"),
            objective(18, 14, "Core - Transverse Waves"),
            objective(19, 15, "Core - Longitudinal Waves"),
          ],
          primaryVideoNames: ["Characteristics and Types of Waves", "Transverse vs. Longitudinal Waves: The Slinky Duality"],
        },
        {
          key: "wave-speed",
          title: "Speed, Frequency & Wavelength",
          intro: "Connect the three measurable quantities that describe how fast a wave pattern moves. Use the wave-speed equation in both directions and make the units part of the reasoning, not an afterthought.",
          keyIdeas: ["Wave speed is related to frequency and wavelength by v = fλ.", "For a fixed wave speed, frequency and wavelength change inversely."],
          objectives: [objective(17, 13, "Core - Wave Speed Equation (v = fλ)")],
          primaryVideoNames: ["Apparent Wavelength", "Characteristics and Types of Waves"],
        },
        {
          key: "reflection-refraction",
          title: "Reflection & Refraction",
          intro: "Treat reflection and refraction as changes in wave behaviour that can be observed and predicted. A ripple tank is especially useful because students can see the wavefronts rather than only read definitions.",
          keyIdeas: ["Reflection changes direction at a boundary.", "Refraction occurs when wave speed changes.", "Ripple-tank observations connect the diagrams to physical behaviour."],
          objectives: [
            objective(20, 16, "Core - Wave Reflection, Refraction and Diffraction"),
            objective(22, 18, "Core - Ripple Tank Wave Phenomena"),
          ],
          primaryVideoNames: ["Superposition and Reflection of Waves", "Huygen`s Principle"],
        },
        {
          key: "diffraction",
          title: "Diffraction",
          intro: "First see waves spread at gaps and edges, then reason about when the spreading becomes most noticeable. Extended material deepens the same idea rather than starting a separate course.",
          keyIdeas: ["Diffraction is the spreading of waves at gaps and edges.", "The amount of diffraction depends on wavelength and gap size."],
          objectives: [
            objective(20, 16, "Core - Wave Reflection, Refraction and Diffraction"),
            objective(22, 18, "Core - Ripple Tank Wave Phenomena"),
            objective(21, 17, "Supp - Diffraction at an Edge", "extended"),
            objective(23, 19, "Supp - Diffraction Through a Gap", "extended"),
          ],
          primaryVideoNames: ["Diffraction of Light"],
        },
      ],
    },
    {
      key: "light",
      title: "Light",
      topicRef: { topicId: 166, sourceTopicId: 34, name: "Light" },
      sections: [
        {
          key: "light-reflection",
          title: "Reflection of Light",
          intro: "Use ray diagrams and mirror observations together so the laws of reflection describe something students can actually see.",
          keyIdeas: ["Angle of incidence equals angle of reflection.", "A plane mirror forms a virtual image with predictable position and size."],
          objectives: [objective(24, 20, "Core - Reflection of Light"), objective(25, 21, "Supp - Reflection of Light", "extended")],
          primaryVideoNames: ["Fermat`s Principle and Reflection by Plane Mirrors", "Image Formation in plane and concave mirror"],
        },
        {
          key: "light-refraction",
          title: "Refraction & Total Internal Reflection",
          intro: "Follow a light ray across a boundary, then build from ordinary refraction to critical angle, total internal reflection and optical-fibre applications.",
          keyIdeas: ["Refraction is caused by a change in wave speed at a boundary.", "Total internal reflection occurs beyond the critical angle under the right conditions."],
          objectives: [objective(26, 22, "Core - Refraction of Light"), objective(27, 23, "Supp - Refraction of Light", "extended")],
          primaryVideoNames: ["Refraction of Light", "Total Internal Reflection"],
        },
        {
          key: "thin-lenses",
          title: "Thin Lenses & Image Formation",
          intro: "Use ray diagrams to connect lens shape, focal point and image formation. Extended learners continue naturally into magnification and vision correction.",
          keyIdeas: ["Converging and diverging lenses bend parallel rays differently.", "Ray diagrams predict image position and characteristics."],
          objectives: [objective(28, 24, "Core - Thin Lenses"), objective(29, 25, "Supp - Thin Lenses", "extended")],
          primaryVideoNames: ["Refraction of Light by Thin Lenses and Lens Formula"],
        },
        {
          key: "dispersion",
          title: "Dispersion of Light",
          intro: "Use a prism to connect white light, colour, wavelength and frequency. Keep the observable phenomenon first; terminology follows from it.",
          keyIdeas: ["A prism separates white light into colours because different wavelengths refract by different amounts."],
          objectives: [objective(30, 26, "Core - Dispersion of Light"), objective(31, 27, "Supp - Dispersion of Light", "extended")],
          primaryVideoNames: ["Dispersion by a prism", "Refraction and Dispersion by Prism"],
        },
      ],
    },
    {
      key: "em-spectrum",
      title: "Electromagnetic spectrum",
      topicRef: { topicId: 167, sourceTopicId: 334, name: "Electromagnetic spectrum" },
      sections: [
        {
          key: "em-family",
          title: "The Electromagnetic Family",
          intro: "Organise the spectrum as one family of waves. Compare wavelength and frequency while keeping the shared speed in vacuum visible throughout.",
          keyIdeas: ["All electromagnetic waves travel at the same speed in vacuum.", "The regions differ mainly in wavelength and frequency."],
          objectives: [objective(32, 28, "Core - Regions of Electromagnetic Spectrum"), objective(33, 29, "Core - Speed of Electromagnetic Waves in Vacuum"), objective(34, 30, "Supp - Speed of EM Waves in Vacuum and Air", "extended")],
          primaryVideoNames: ["Electromagnetic Spectrum"],
        },
        {
          key: "em-uses-hazards",
          title: "Uses & Hazards",
          intro: "Compare why different regions are useful and what makes excessive exposure dangerous. The point is to connect physical properties to real applications and risk.",
          keyIdeas: ["Different parts of the spectrum are useful because they interact with matter differently.", "Hazards depend on the type and amount of radiation absorbed."],
          objectives: [objective(35, 31, "Core - Applications of Electromagnetic Spectrum"), objective(36, 32, "Core - Hazards of Electromagnetic Radiation")],
          primaryVideoNames: ["Electromagnetic Spectrum"],
        },
        {
          key: "em-communication",
          title: "Communication & Signals",
          intro: "Move from satellite communication into the broader question of how information is carried. Extended material then compares analogue and digital signals and why regeneration matters.",
          keyIdeas: ["Microwaves are widely used for satellite communication.", "Digital signalling can be regenerated accurately over long distances."],
          objectives: [
            objective(37, 33, "Core - Satellite Communication via Microwaves"),
            objective(38, 34, "Supp - EM Waves in Communication Systems", "extended"),
            objective(39, 35, "Supp - Digital vs Analogue Signals", "extended"),
            objective(40, 36, "Supp - Transmission of Sound Signals", "extended"),
            objective(41, 37, "Supp - Benefits of Digital Signalling", "extended"),
          ],
          primaryVideoNames: ["Electromagnetic Spectrum", "Elements of a Communication System"],
        },
      ],
    },
    {
      key: "sound",
      title: "Sound",
      topicRef: { topicId: 168, sourceTopicId: 570, name: "Sound" },
      sections: [
        {
          key: "sound-production",
          title: "Producing & Transmitting Sound",
          intro: "Begin with vibration, then trace how a longitudinal disturbance travels through a medium. Extended learners add the particle picture of compressions and rarefactions.",
          keyIdeas: ["Sound is produced by vibrating sources.", "Sound is longitudinal and requires a medium."],
          objectives: [objective(42, 38, "Core - Production of Sound by Vibrations"), objective(43, 39, "Core - Longitudinal Nature of Sound"), objective(44, 40, "Supp - Compressions and Rarefactions", "extended"), objective(46, 42, "Core - Medium for Sound Transmission")],
          primaryVideoNames: ["Wavemotion and Production of sound", "The Illusion of Sound"],
        },
        {
          key: "sound-speed",
          title: "Speed of Sound",
          intro: "Connect the approximate speed in air with a practical measurement method, then compare how transmission changes across solids, liquids and gases.",
          keyIdeas: ["Sound speed can be measured from distance and travel time.", "Sound generally travels fastest in solids and slowest in gases."],
          objectives: [objective(47, 43, "Supp - Speed of Sound in Different Media", "extended"), objective(48, 44, "Core - Speed of Sound in Air"), objective(49, 45, "Core - Measurement of Speed of Sound in Air")],
          primaryVideoNames: ["Wavemotion and Production of sound"],
        },
        {
          key: "pitch-loudness",
          title: "Hearing, Pitch & Loudness",
          intro: "Relate the measurable features of a wave to what we hear. Frequency connects to pitch; amplitude connects to loudness; the audible range gives those ideas a human scale.",
          keyIdeas: ["Frequency determines pitch.", "Amplitude affects loudness.", "Human hearing has a limited frequency range."],
          objectives: [objective(45, 41, "Core - Human Audible Frequency Range"), objective(50, 46, "Core - Pitch and Loudness of Sound")],
          primaryVideoNames: ["Dimensions of Sound", "Range of hearing (Sonics) and Ultrasonics"],
        },
        {
          key: "echo-ultrasound",
          title: "Echoes & Ultrasound",
          intro: "Use reflection of sound to explain echoes, then extend the same wave ideas to ultrasound in imaging, testing and distance measurement.",
          keyIdeas: ["An echo is reflected sound.", "Ultrasound has frequency above the upper limit of human hearing and has important practical uses."],
          objectives: [objective(51, 47, "Core - Echoes and Reflection of Sound"), objective(52, 48, "Core - Concept and Definition of Ultrasound"), objective(53, 49, "Supp - Applications and Calculations of Ultrasound", "extended")],
          primaryVideoNames: ["Reflection of sound", "Range of hearing (Sonics) and Ultrasonics"],
        },
      ],
    },
  ],
};
